import {
	type BranchProductSalesChannelItemQopFilterInput,
	useBranchProductSalesChannelItemsQuery,
} from "@/generated/graphql";
import type { PaginationState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";

export interface BranchProductSalesChannelItemRow {
	branchProductSalesChannelItemID: string;
	branchID: string;
	branchCode: string;
	productID: string;
	productCode: string;
	productName: string;
	product?: { barcode: string } | null;
	salesChannelID: string;
	salesChannelCategoryGroupID?: string | null;
	type: string;
	note: string;
}

// Server-paged items for one menu group. `type` is optional so the table can
// show manual exclusions and cron-driven out-of-stock rows side by side.
export const useBranchProductSalesChannelItemList = (
	groupID: string | undefined,
	type?: string,
	initialFilters?: Record<string, unknown>,
) => {
	const [paginationState, setPaginationState] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: 10,
	});
	const [filtersState, setFiltersState] = useState<Record<string, unknown>>(
		() => initialFilters ?? {},
	);

	const options = useMemo(
		() => ({
			variables: {
				qop: {
					pagination: {
						page: paginationState.pageIndex + 1,
						pageSize: paginationState.pageSize,
						withCount: true,
					},
					filters: {
						// Hook-level type first: a UI filter must be able to override it.
						type: type ?? null,
						...filtersState,
						salesChannelCategoryGroupID: groupID ?? null,
					} as BranchProductSalesChannelItemQopFilterInput,
				},
			},
			pause: !groupID,
			requestPolicy: "network-only" as const,
			context: { suspense: false },
		}),
		[
			paginationState.pageIndex,
			paginationState.pageSize,
			filtersState,
			groupID,
			type,
		],
	);

	const [{ data, error, fetching }, fetch] =
		useBranchProductSalesChannelItemsQuery(options);

	const load = useCallback(
		() => fetch({ requestPolicy: "network-only" }),
		[fetch],
	);

	const setPagination = useCallback(
		(next: PaginationState) => setPaginationState(next),
		[],
	);

	const setFilters = useCallback((next: Record<string, unknown>) => {
		setFiltersState(next);
		setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
	}, []);

	return {
		items: data?.branchProductSalesChannelItems.items ?? [],
		rowCount: data?.branchProductSalesChannelItems.pagination.totalRows ?? 0,
		fetching,
		error,
		load,
		paginationState,
		setPagination,
		filtersState,
		setFilters,
	};
};
